import { FormMessage } from '../types';

/**
 * Copy for a join-window picker's own validation failure, keyed by the reason
 * MUI reports. Lives here rather than in the component so it can be read without
 * rendering a date picker.
 */
export const getJoinWindowPickerError = (
  field: 'start' | 'end',
  reason: string | null,
): string | undefined => {
  if (!reason) return undefined;
  if (reason === 'disablePast' || reason === 'minDate') {
    return `Join window ${field} cannot be in the past.`;
  }
  if (reason === 'minDateTime' && field === 'end') {
    return 'Join window end must be after the start.';
  }
  return `Enter a valid ${field} date and time.`;
};

/**
 * Position of the first enrollment FOLLOW-UP message: the first message after
 * position 1 that is anchored to something other than the join flow.
 * Everything below that position is a join message.
 */
function getEnrollmentScheduledStartPosition(
  messages: Pick<FormMessage, 'position' | 'scheduledType'>[],
): number | null {
  if (!messages.length) return null;

  const ordered = [...messages].sort((a, b) => a.position - b.position);
  const firstScheduled = ordered.find(
    message =>
      message.position > 1 &&
      (message.scheduledType === 'time' ||
        message.scheduledType === 'reply' ||
        message.scheduledType === 'contact_added'),
  );

  return firstScheduled?.position ?? null;
}

/**
 * Tag each message as a join or follow-up message so the builder can render the
 * "Join Messages" / "Follow Up Messages" dividers. The split rides along on the
 * form messages, which is why loading a saved campaign has to derive it — without
 * this, every message loads as a join message and the "Add Follow Up Message(s)"
 * button reappears on campaigns that already have follow-ups.
 */
export function assignEnrollmentSections(messages: FormMessage[]): FormMessage[] {
  const startPosition = getEnrollmentScheduledStartPosition(messages);
  return messages.map(message => ({
    ...message,
    enrollmentSection:
      startPosition !== null && message.position >= startPosition ? 'scheduled' : 'join',
  }));
}

/** Schedules a join message below the follow-up boundary is allowed to hold. */
const JOIN_SCHEDULES = ['immediate', 'delay'];
/** Schedules a follow-up after the first one is allowed to hold. */
const FOLLOW_UP_SCHEDULES = ['immediate', 'delay', 'time', 'reply'];

/**
 * Force every enrollment message onto a schedule its position actually offers.
 *
 * Runs for saved campaigns too, so anything stored outside the rules is rewritten
 * before the user sees it. Without it the Schedule dropdown can open showing a
 * value that is greyed out — or, for a `contact_added` message, blank, since the
 * enrollment option list has no such entry — and position 1 can display a
 * schedule that differs from the `immediate` the payload actually sends.
 *
 * Sections are read as given, not recomputed: the split comes from the stored
 * values and only then are schedules coerced, so a join message saved as `reply`
 * still counts as the start of the follow-up section.
 */
export function normalizeEnrollmentSchedules(messages: FormMessage[]): FormMessage[] {
  const followUps = messages.filter(m => m.enrollmentSection === 'scheduled');
  const firstFollowUpPosition = followUps.length
    ? Math.min(...followUps.map(m => m.position))
    : null;

  return messages.map(message => {
    const { position, scheduledType, enrollmentSection } = message;

    // Position 1 opens the window, so its schedule is the window's own start.
    if (position === 1) {
      return scheduledType === 'immediate'
        ? message
        : { ...message, scheduledType: 'immediate' as const, delay: null, time: null };
    }

    // The message that opens the follow-up section is scheduled by wall-clock
    // time. `time` itself is left alone — only the schedule kind is forced.
    if (position === firstFollowUpPosition) {
      return scheduledType === 'time'
        ? message
        : { ...message, scheduledType: 'time' as const, delay: null };
    }

    const allowed = enrollmentSection === 'scheduled' ? FOLLOW_UP_SCHEDULES : JOIN_SCHEDULES;
    return allowed.includes(scheduledType)
      ? message
      : { ...message, scheduledType: 'immediate' as const, delay: null, time: null };
  });
}
